import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Tooltip,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Business,
  People,
  Assessment,
  Settings,
  Logout,
  Notifications,
  AccountCircle,
  LocalShipping,
  Nature,
  ChevronLeft,
  ChevronRight,
  CloudUpload,
  FolderOpen,
  Folder,
  Assignment,
  ExpandMore,
  Code as ApiIcon,
  Storage as DataIcon,
  FlightTakeoff as AirportIcon,
  DirectionsBoat as SeaportIcon,
  LocationOn as PostalCodeIcon,
  Route as DistanceIcon,
  LocationCity as CityIcon,
  Home as AddressIcon,
  CardMembership as CertificateIcon,
  BarChart as StatisticsIcon,
  History,
  Announcement,
  PictureAsPdf,
  MenuBook as LibraryIcon,
  QuestionAnswer as FaqNavIcon,
  AutoStories as KbIcon,
  BugReport as BugReportIcon,
  RateReview as FeedbackIcon,
} from '@mui/icons-material';
import Logo from '../common/Logo';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { AccountType } from '../../types/auth';
import { companyApi } from '../../services/api/companyApi';
import { apiClient } from '../../services/api/authApi';
import ccLogoInverted from '../../assets/images/carboncare_logo_landscape_inverted.svg';
import ccLogoInvertedSmall from '../../assets/images/carboncare_logo_small_inverted.svg';
import { useNotifications } from '../../hooks/useNotifications';
import NotificationPanel from '../notifications/NotificationPanel';
import FeedbackDialog, { FeedbackDialogMode } from '../common/FeedbackDialog';

const drawerWidth = 260;
const collapsedDrawerWidth = 90;

interface NavigationItem {
  title: string;
  icon: React.ReactElement;
  path?: string;
  requiredRole?: AccountType;
  excludedRoles?: AccountType[]; // Roles that should NOT see this item
  children?: NavigationItem[];
}

const navigationItems: NavigationItem[] = [
  {
    title: 'Home Screen',
    icon: <Dashboard />,
    path: '/dashboard',
    requiredRole: AccountType.SubCompanyAdmin,
    excludedRoles: [AccountType.SubCustomerAdmin, AccountType.BranchAdmin],
  },
  {
    title: 'Shipments',
    icon: <LocalShipping />,
    path: '/shipments',
    requiredRole: AccountType.User,
  },
  {
    title: 'XML API Testing',
    icon: <ApiIcon />,
    path: '/xml-api',
    requiredRole: AccountType.User,
    excludedRoles: [AccountType.SubCustomerAdmin, AccountType.BranchAdmin, AccountType.SubCompanyAdmin],
  },
  {
    title: 'Knowledge Base',
    icon: <KbIcon />,
    children: [
      {
        title: 'Overview',
        icon: <KbIcon />,
        path: '/knowledge-base',
      },
      {
        title: 'Document Library',
        icon: <LibraryIcon />,
        path: '/knowledge-base/library',
      },
      {
        title: 'FAQ',
        icon: <FaqNavIcon />,
        path: '/knowledge-base/faq',
      },
      {
        title: 'API Documentation',
        icon: <ApiIcon />,
        path: '/knowledge-base/api-docs',
        requiredRole: AccountType.User,
        excludedRoles: [AccountType.SubCustomerAdmin, AccountType.BranchAdmin, AccountType.SubCompanyAdmin],
      },
    ],
  },
  {
    title: 'Data Management',
    icon: <DataIcon />,
    requiredRole: AccountType.CarbonCareAdmin,
    excludedRoles: [AccountType.SubCustomerAdmin, AccountType.BranchAdmin, AccountType.SubCompanyAdmin], // Only MasterAdmin can access
    children: [
      {
        title: 'Airports',
        icon: <AirportIcon />,
        path: '/data-management/airports',
      },
      {
        title: 'Seaports',
        icon: <SeaportIcon />,
        path: '/data-management/seaports',
      },
      {
        title: 'Postal Codes',
        icon: <PostalCodeIcon />,
        path: '/data-management/postal-codes',
      },
      {
        title: 'Cities',
        icon: <CityIcon />,
        path: '/data-management/cities',
      },
      {
        title: 'Addresses',
        icon: <AddressIcon />,
        path: '/data-management/addresses',
      },
      {
        title: 'Distance Cache',
        icon: <DistanceIcon />,
        path: '/data-management/distance-cache',
      },
    ],
  },
  {
    title: 'Statistics',
    icon: <StatisticsIcon />,
    requiredRole: AccountType.LicenseeAdmin,
    children: [
      {
        title: 'Overview',
        icon: <StatisticsIcon />,
        path: '/statistics/overview',
        requiredRole: AccountType.CarbonCareAdmin,
      },
      {
        title: 'Licensee Stats',
        icon: <Business />,
        path: '/statistics/company',
        requiredRole: AccountType.LicenseeAdmin,
      },
    ],
  },
  {
    title: 'GHG Reporting',
    icon: <Nature />,
    requiredRole: AccountType.LicenseeAdmin,
    children: [
      {
        title: 'Overview',
        icon: <Nature />,
        path: '/ghg-reporting/overview',
        requiredRole: AccountType.CarbonCareAdmin,
      },
      {
        title: 'Licensee Report',
        icon: <Business />,
        path: '/ghg-reporting/company',
        requiredRole: AccountType.LicenseeAdmin,
      },
    ],
  },
  {
    title: 'File Management',
    icon: <FolderOpen />,
    requiredRole: AccountType.User,
    children: [
      {
        title: 'Upload New Files',
        icon: <CloudUpload />,
        path: '/excel-upload',
        requiredRole: AccountType.LicenseeAdmin,
      },
      {
        title: 'Uploaded Files',
        icon: <Folder />,
        path: '/files/uploaded',
        requiredRole: AccountType.User,
      },
      {
        title: 'Processing Files',
        icon: <Assignment />,
        path: '/files/processing',
        requiredRole: AccountType.CarbonCareAdmin,
      },
      {
        title: 'Calculated Files',
        icon: <Assessment />,
        path: '/files/calculated',
        requiredRole: AccountType.CarbonCareAdmin,
      },
      {
        title: 'Final Files',
        icon: <Folder />,
        path: '/files/final',
        requiredRole: AccountType.User,
      },
    ],
  },
  {
    title: 'Analytics',
    icon: <Assessment />,
    children: [
      {
        title: 'Analytics Charts',
        icon: <Assessment />,
        path: '/analytics',
      },
      {
        title: 'Analytics Data',
        icon: <Assessment />,
        path: '/emissions-dashboard',
      },
    ],
  },
  {
    title: 'Licensees',
    icon: <Business />,
    path: '/companies',
    requiredRole: AccountType.SubCompanyAdmin,
    excludedRoles: [AccountType.SubCustomerAdmin, AccountType.BranchAdmin], // These roles manage specific segments, not companies
  },
  // {
  //   title: 'Licensee Management',
  //   icon: <Business />,
  //   requiredRole: AccountType.SubCompanyAdmin,
  //   excludedRoles: [AccountType.SubCustomerAdmin, AccountType.BranchAdmin], // These roles manage specific segments, not companies
  //   children: [
  //     {
  //       title: 'Licensees',
  //       icon: <Business />,
  //       path: '/companies',
  //     },
  //     // {
  //     //   title: 'Hierarchy View',
  //     //   icon: <AccountTree />,
  //     //   path: '/hierarchy',
  //     // },
  //   ],
  // },
  {
    title: 'Users',
    icon: <People />,
    path: '/users',
    requiredRole: AccountType.LicenseeAdmin,
  },
  {
    title: 'Certificates',
    icon: <CertificateIcon />,
    path: '/certificates',
    requiredRole: AccountType.SubCompanyAdmin,
  },
  // {
  //   title: 'Carbon Reports',
  //   icon: <Nature />,
  //   path: '/reports',
  // },
  {
    title: 'Admin Updates',
    icon: <Announcement />,
    path: '/admin/updates',
    requiredRole: AccountType.MasterAdmin,
  },
  {
    title: 'Report Scheduler',
    icon: <PictureAsPdf />,
    path: '/admin/report-scheduler',
    requiredRole: AccountType.MasterAdmin,
  },
  {
    title: 'Audit Logs',
    icon: <History />,
    path: '/audit-logs',
    requiredRole: AccountType.CarbonCareAdmin,
    excludedRoles: [AccountType.SubCustomerAdmin, AccountType.BranchAdmin, AccountType.SubCompanyAdmin],
  },
  {
    title: 'Settings',
    icon: <Settings />,
    path: '/settings',
  },
];

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [hoverMenu, setHoverMenu] = useState<{ item: NavigationItem; anchorEl: HTMLElement } | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [company, setCompany] = useState<any>(null);
  const [supportMenuAnchorEl, setSupportMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackDialogMode, setFeedbackDialogMode] = useState<FeedbackDialogMode>('bug');

  // Notification management
  const NOTIFICATION_PAGE_SIZE = 10;
  const {
    notifications,
    unreadCount,
    loading: notificationsLoading,
    loadingMore: notificationsLoadingMore,
    hasMore: notificationsHasMore,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh: refreshNotifications,
    syncLatest: syncNotifications,
    loadMore: loadMoreNotifications,
  } = useNotifications({ pageSize: NOTIFICATION_PAGE_SIZE });

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  // Fetch company data
  useEffect(() => {
    const fetchCompanyData = async () => {
      const token = localStorage.getItem('authToken');
      if (user?.companyId && token) {
        try {
          const response = await companyApi.getById(user.companyId);
          setCompany(response);
        } catch (error) {
          console.error('Failed to fetch company data:', error);
        }
      }
    };

    fetchCompanyData();
  }, [user?.companyId]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
    // Pull anything the 30 s poller hasn't picked up yet, silently.
    syncNotifications();
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await markAsRead(id);
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const handleDeleteNotification = async (id: string) => {
    try {
      await deleteNotification(id);
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/auth/login');
    handleProfileMenuClose();
  };

  const handleStopImpersonating = async () => {
    try {
      const response = await apiClient.post('/auth/stop-impersonating');
      if (response.data.success) {
        // Update auth store with original user and token
        const authStore = useAuthStore.getState();
        authStore.setAuth(response.data.data.user, response.data.data.token);

        // Redirect to dashboard
        globalThis.location.href = '/dashboard';
      }
    } catch (error) {
      console.error('Failed to stop impersonating:', error);
    }
  };

  const handleSupportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSupportMenuAnchorEl(event.currentTarget);
  };

  const handleSupportMenuClose = () => {
    setSupportMenuAnchorEl(null);
  };

  const handleOpenFeedbackDialog = (mode: FeedbackDialogMode) => {
    setSupportMenuAnchorEl(null);
    setFeedbackDialogMode(mode);
    setFeedbackDialogOpen(true);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const toggleExpandItem = (title: string) => {
    setExpandedItems(prev =>
      prev.includes(title)
        ? prev.filter(item => item !== title)
        : [...prev, title]
    );
  };

  const handleHoverMenuOpen = (item: NavigationItem, event: React.MouseEvent<HTMLElement>) => {
    if (sidebarCollapsed && item.children && item.children.length > 0) {
      // Clear any existing timeout
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        setHoverTimeout(null);
      }
      setHoverMenu({ item, anchorEl: event.currentTarget });
    }
  };

  const handleHoverMenuClose = () => {
    const timeout = setTimeout(() => {
      setHoverMenu(null);
    }, 500); // Much longer delay to give time to move mouse to menu
    setHoverTimeout(timeout);
  };

  const handleHoverMenuKeepOpen = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  const isItemExpanded = (title: string) => expandedItems.includes(title);

  const isPathActive = (item: NavigationItem, currentPath: string): boolean => {
    if (item.path === currentPath) return true;
    if (item.children) {
      return item.children.some(child => child.path === currentPath);
    }
    return false;
  };

  const filteredNavigationItems = navigationItems.filter(item => {
    // Check if user's role is excluded
    if (item.excludedRoles && user && item.excludedRoles.includes(user.accountType)) {
      return false;
    }
    // Check if user meets required role
    if (item.requiredRole && !(user && user?.accountType >= item.requiredRole)) {
      return false;
    }

    // MasterAdmin and CarbonCareAdmin bypass company feature checks
    const bypassFeatureChecks = user && user.accountType >= AccountType.CarbonCareAdmin;

    // Hide Analytics if company analytics is disabled
    if (!bypassFeatureChecks && company && (company.analytics !== true)) {
      if (item.title === 'Analytics') {
        return false;
      }
    }

    // Hide Certificates if company certificate_available is disabled
    if (!bypassFeatureChecks && company && (company.certificateAvailable !== true && company.certificate_available !== true)) {
      if (item.path === '/certificates') {
        return false;
      }
    }

    return true;
  });

  const currentDrawerWidth = sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;

  const isReportMode = new URLSearchParams(location.search).get('report_mode') === 'true';
  if (isReportMode) {
    return (
      <Box sx={{ p: 2, background: '#fff', minHeight: '100vh' }}>
        {children}
      </Box>
    );
  }

  const drawer = (
    <Box
      sx={{
        height: '100%',
        background: '#ffffff',
        borderRight: '1px solid #e5e7eb',
        color: '#1f2937',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 70,
          px: 1.5,
            py: 1.5,
            background: '#6c757d',
            borderBottom: '1px solid #5a6268',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: sidebarCollapsed ? 1 : 0,
              transition: theme.transitions.create('opacity', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
            }}
          >
            <Logo src={ccLogoInvertedSmall} width={50} variant="white-text" />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: sidebarCollapsed ? 0 : 1,
              transition: theme.transitions.create('opacity', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
            }}
          >
            <Logo src={ccLogoInverted} width={195} variant="white-text" />
          </Box>
        </Box>

      <List sx={{ pl: 3, pr: 1, py: 2, flex: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: '4px' }, '&::-webkit-scrollbar-track': { background: 'transparent' }, '&::-webkit-scrollbar-thumb': { background: '#d1d5db', borderRadius: '2px', '&:hover': { background: '#9ca3af' } } }}>
        {filteredNavigationItems.map((item, index) => (
          <React.Fragment key={item.title}>
            <ListItem disablePadding sx={{ mb: 0.5 }}>
              <Tooltip
                title={item.title}
                placement="right"
                disableHoverListener={!sidebarCollapsed}
                arrow
              >
                <ListItemButton
                  onClick={(e) => {
                    if (item.path) {
                      handleNavigation(item.path);
                    } else if (item.children && sidebarCollapsed) {
                      // Show menu on click when collapsed - don't close it immediately
                      e.preventDefault();
                      if (hoverMenu) {
                        setHoverMenu(null); // Close if already open
                      } else {
                        handleHoverMenuOpen(item, e);
                      }
                    } else if (item.children && !sidebarCollapsed) {
                      toggleExpandItem(item.title);
                      const firstAccessibleChild = item.children.find(subItem => {
                        if (subItem.excludedRoles && user && subItem.excludedRoles.includes(user.accountType)) return false;
                        if (!subItem.requiredRole) return true;
                        return user && user.accountType >= subItem.requiredRole;
                      });
                      if (firstAccessibleChild?.path) {
                        handleNavigation(firstAccessibleChild.path);
                      }
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (sidebarCollapsed && item.children && item.children.length > 0) {
                      handleHoverMenuOpen(item, e);
                    }
                  }}
                  onMouseLeave={() => {
                    // Only close on mouse leave if we're not hovering over the menu
                    if (sidebarCollapsed && hoverMenu) {
                      handleHoverMenuClose();
                    }
                  }}
                  selected={!item.children && isPathActive(item, location.pathname)}
                  disableRipple={!!item.children}
                  sx={{
                    borderRadius: '8px',
                    minHeight: 40,
                    justifyContent: 'flex-start',
                    pl: 2,
                    pr: 0.5,
                    py: 1,
                    background: isPathActive(item, location.pathname) ? '#f9fafb' : 'transparent',
                    color: isPathActive(item, location.pathname) ? theme.palette.primary.main : theme.palette.text.primary,
                    transition: theme.transitions.create(['color'], {
                      easing: theme.transitions.easing.sharp,
                      duration: theme.transitions.duration.leavingScreen,
                    }),
                    '&:hover': {
                      background: 'transparent',
                      color: theme.palette.primary.main,
                    },
                    '&.Mui-selected, &.Mui-selected:hover': {
                      backgroundColor: 'transparent',
                    },
                    ...(item.children && {
                      '&:active': { background: 'transparent' },
                      '&.Mui-focusVisible': { background: 'transparent' },
                    }),
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flex: 1,
                      minWidth: 0,
                      borderRadius: '6px',
                      px: 0.5,
                      mx: -0.5,
                      py: 0.5,
                      my: -0.5,
                      transition: 'background 0.15s ease',
                      '&:hover': {
                        background: '#f9fafb',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 2,
                        justifyContent: 'center',
                        color: 'inherit',
                        fontSize: '20px',
                        position: 'relative',
                        transform: sidebarCollapsed ? 'translateX(-8px)' : 'translateX(0)',
                        transition: theme.transitions.create(['color', 'transform'], {
                          easing: theme.transitions.easing.sharp,
                          duration: theme.transitions.duration.leavingScreen,
                        }),
                      }}
                    >
                      {item.icon}
                      {/* Show indicator for items with children when collapsed */}
                      {sidebarCollapsed && item.children && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -2,
                            right: -2,
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: theme.palette.primary.main,
                          }}
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.title}
                      sx={{
                        my: 0,
                        '& .MuiListItemText-primary': {
                          fontSize: '14px',
                          fontWeight: isPathActive(item, location.pathname) ? 600 : 500,
                          color: 'inherit',
                          letterSpacing: '-0.01em',
                          lineHeight: 1.5,
                          fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
                          transition: 'color 0.15s ease',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }
                      }}
                    />
                  </Box>
                  {item.children && (
                    <Box
                      sx={{
                        color: 'inherit',
                        fontSize: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        ml: 1,
                        pl: '8px',
                        pr: '8px',
                        py: '4px',
                        borderRadius: '6px',
                        flexShrink: 0,
                        opacity: sidebarCollapsed ? 0 : 1,
                        maxWidth: sidebarCollapsed ? 0 : 60,
                        overflow: 'hidden',
                        pointerEvents: sidebarCollapsed ? 'none' : 'auto',
                        transition: sidebarCollapsed
                          ? `opacity 150ms ${theme.transitions.easing.sharp} 150ms, max-width ${theme.transitions.duration.leavingScreen}ms ${theme.transitions.easing.sharp} 150ms, background 150ms ${theme.transitions.easing.sharp}, padding 150ms ${theme.transitions.easing.sharp}`
                          : theme.transitions.create(['opacity', 'max-width', 'background', 'padding'], {
                              easing: theme.transitions.easing.sharp,
                              duration: theme.transitions.duration.enteringScreen,
                            }),
                        '&:hover': {
                          background: '#f9fafb',
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpandItem(item.title);
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'transform 0.2s ease',
                          transform: isItemExpanded(item.title) ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                      >
                        <ExpandMore fontSize="small" />
                      </Box>
                    </Box>
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>

            {/* Enhanced Sub-menu items */}
            {item.children && !sidebarCollapsed && isItemExpanded(item.title) && (
              <List sx={{ pl: 0, pt: 0.5, pb: 0.5 }}>
                {item.children.filter(subItem => {
                  if (subItem.excludedRoles && user && subItem.excludedRoles.includes(user.accountType)) return false;
                  if (!subItem.requiredRole) return true;
                  return user && user.accountType >= subItem.requiredRole;
                }).map((subItem, subIndex) => (
                  <ListItem key={subItem.title} disablePadding>
                    <ListItemButton
                      onClick={() => subItem.path && handleNavigation(subItem.path)}
                      selected={location.pathname === subItem.path}
                      sx={{
                        pl: 6,
                        pr: 2,
                        py: 0.75,
                        borderRadius: '8px',
                        minHeight: 36,
                        background: 'transparent',
                        color: location.pathname === subItem.path ? theme.palette.primary.main : theme.palette.text.primary,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          background: '#f9fafb',
                          color: theme.palette.primary.main,
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      <ListItemText
                        primary={subItem.title}
                        sx={{
                          '& .MuiListItemText-primary': {
                            fontSize: '13px',
                            fontWeight: location.pathname === subItem.path ? 600 : 500,
                            color: 'inherit',
                            letterSpacing: '-0.01em',
                            lineHeight: 1.5,
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
                            transition: 'color 0.15s ease',
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </React.Fragment>
        ))}
      </List>

      {/* Hover Menu for Collapsed Sidebar */}
      {hoverMenu && sidebarCollapsed && (
        <Menu
          anchorEl={hoverMenu.anchorEl}
          open={Boolean(hoverMenu)}
          onClose={(event, reason) => {
            // Only close on escape key or backdrop click, not on menu item click
            if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
              setHoverMenu(null);
            }
          }}
          disableAutoFocusItem
          disableRestoreFocus
          MenuListProps={{
            'aria-labelledby': 'basic-button',
            onMouseEnter: handleHoverMenuKeepOpen,
            onMouseLeave: handleHoverMenuClose,
            sx: {
              py: 1,
              '& .MuiMenuItem-root': {
                px: 3,
                py: 1.5,
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.1)',
                }
              }
            }
          }}
          slotProps={{
            paper: {
              onMouseEnter: handleHoverMenuKeepOpen,
              onMouseLeave: handleHoverMenuClose,
              sx: {
                background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                backdropFilter: 'blur(20px) saturate(180%)',
                border: `1px solid ${theme.palette.secondary.light}40`,
                color: 'white',
                minWidth: 220,
                ml: 0.5,
                boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.24)',
                borderRadius: 3,
              }
            }
          }}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
        >
          {hoverMenu.item.children?.map((subItem) => (
            <MenuItem
              key={subItem.title}
              onClick={() => {
                if (subItem.path) handleNavigation(subItem.path);
                handleHoverMenuClose();
              }}
              sx={{
                px: 3,
                py: 1.5,
                minHeight: 44,
                background: location.pathname === subItem.path
                  ? 'linear-gradient(135deg, rgba(144, 191, 69, 0.25), rgba(144, 191, 69, 0.15))'
                  : 'transparent',
                border: location.pathname === subItem.path
                  ? `1px solid ${theme.palette.primary.main}30`
                  : '1px solid transparent',
                borderRadius: 2,
                mx: 1,
                my: 0.5,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: location.pathname === subItem.path
                    ? 'linear-gradient(135deg, rgba(144, 191, 69, 0.35), rgba(144, 191, 69, 0.25))'
                    : 'rgba(255, 255, 255, 0.1)',
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    color: location.pathname === subItem.path
                      ? theme.palette.primary.main
                      : 'rgba(255, 255, 255, 0.8)',
                    fontSize: '18px',
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {subItem.icon}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: location.pathname === subItem.path ? 600 : 500,
                    color: location.pathname === subItem.path
                      ? theme.palette.primary.main
                      : 'rgba(255, 255, 255, 0.9)',
                    letterSpacing: '0.3px',
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {subItem.title}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <Tooltip
        title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        placement="right"
        arrow
      >
        <IconButton
          onClick={handleSidebarToggle}
          size="small"
          sx={{
            position: 'fixed',
            top: 35,
            left: `${currentDrawerWidth}px`,
            transform: 'translate(-50%, -50%)',
            zIndex: (t) => t.zIndex.drawer + 2,
            width: 28,
            height: 28,
            backgroundColor: '#ffff',
            border: '2px solid #ffffff',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
            color: '#1f2937',
            '&:hover': {
              backgroundColor: '#ffff',
              color: '#1f2937',
              borderColor: '#ffffff',
            },
            transition: 'all 0.2s ease',
            display: { xs: 'none', lg: 'inline-flex' },
          }}
        >
          {sidebarCollapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
        </IconButton>
      </Tooltip>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { lg: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { lg: `${currentDrawerWidth}px` },
          backgroundColor: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(20px) saturate(180%)',
          color: 'text.primary',
          borderRadius: 0,
          borderBottom: `1px solid ${theme.palette.primary.main}15`,
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.06)',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          '@media print': {
            display: 'none',
          },
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { lg: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            {(() => {
              // Find current page title + icon (top-level match first, then child match, else default)
              let title = 'Home Screen';
              let icon: React.ReactElement = <Dashboard />;
              for (const item of navigationItems) {
                if (item.path === location.pathname) {
                  title = item.title;
                  icon = item.icon;
                  break;
                }
                if (item.children) {
                  const subItem = item.children.find(child => child.path === location.pathname);
                  if (subItem) {
                    title = subItem.title;
                    icon = subItem.icon;
                    break;
                  }
                }
              }
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box
                    sx={{
                      color: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      '& .MuiSvgIcon-root': { fontSize: 26 },
                    }}
                  >
                    {icon}
                  </Box>
                  <Typography
                    noWrap
                    component="div"
                    sx={{
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      color: 'text.primary',
                      letterSpacing: '-0.01em',
                      lineHeight: 1.2,
                    }}
                  >
                    {title}
                  </Typography>
                </Box>
              );
            })()}
            {/* Impersonation Indicator */}
            {user?.isImpersonating && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.5,
                borderRadius: 2,
                backgroundColor: 'warning.light',
                color: 'warning.contrastText'
              }}>
                <Typography variant="body2" fontWeight="bold">
                  Impersonating: {user.firstName} {user.lastName}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  onClick={handleStopImpersonating}
                  sx={{ ml: 1 }}
                >
                  Stop Impersonating
                </Button>
              </Box>
            )}
          </Box>

          <Tooltip title="Notifications" arrow>
            <IconButton
              size="large"
              aria-label="show notifications"
              color="inherit"
              onClick={handleNotificationMenuOpen}
            >
              <Badge badgeContent={unreadCount} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title="Report bug or share feedback" arrow>
            <IconButton
              size="large"
              aria-label="report or feedback"
              color="inherit"
              onClick={handleSupportMenuOpen}
              sx={{ ml: 0.5 }}
            >
              <BugReportIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Account menu" arrow>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
              sx={{ ml: 1 }}
            >
              {(() => {
                if (user?.profileImageUrl) return (
                <Avatar
                  src={user.profileImageUrl}
                  alt={`${user.firstName} ${user.lastName}`}
                  sx={{
                    width: 32,
                    height: 32
                  }}
                />
                );
                if (company?.logoUrl || company?.logo_url) return (
                <Avatar
                  src={company.logoUrl || company.logo_url}
                  alt={company.name || 'Licensee logo'}
                  sx={{
                    width: 32,
                    height: 32
                  }}
                />
                );
                if (user?.firstName) return (
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: theme.palette.primary.main,
                    fontSize: 15
                  }}
                >
                  {user.firstName.charAt(0)}{user.lastName?.charAt(0)}
                </Avatar>
                );
                return (
                <AccountCircle />
                );
              })()}
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px) saturate(180%)',
              border: `1px solid ${theme.palette.primary.main}12`,
              borderRadius: 2.5,
              boxShadow: `0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px ${theme.palette.primary.main}08`,
              mt: 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '& .MuiMenuItem-root': {
                borderRadius: 1.5,
                mx: 1,
                my: 0.5,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.primary.main}08)`,
                  transform: 'translateX(4px)',
                },
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => navigate('/profile')} sx={{ py: 1.25 }}>
          {(() => {
            if (user?.profileImageUrl) return (
            <Avatar src={user.profileImageUrl} alt={`${user.firstName} ${user.lastName}`} />
            );
            if (company?.logoUrl || company?.logo_url) return (
            <Avatar src={company.logoUrl || company.logo_url} alt={company.name || 'Company logo'} />
            );
            return (
            <Avatar sx={{ backgroundColor: theme.palette.primary.main, fontSize: 15 }}>
              {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
            </Avatar>
            );
          })()}
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {company?.name || 'Licensee'}
            </Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => navigate('/settings')}>
          <Settings sx={{ mr: 2 }} />
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <Logout sx={{ mr: 2 }} />
          Logout
        </MenuItem>
      </Menu>

      {/* Support Menu — Report Bug / Share Feedback */}
      <Menu
        id="support-menu"
        anchorEl={supportMenuAnchorEl}
        open={Boolean(supportMenuAnchorEl)}
        onClose={handleSupportMenuClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px) saturate(180%)',
              border: `1px solid ${theme.palette.primary.main}12`,
              borderRadius: 2.5,
              boxShadow: `0 8px 32px rgba(0, 0, 0, 0.12)`,
              mt: 1.5,
              '& .MuiMenuItem-root': {
                borderRadius: 1.5,
                mx: 1,
                my: 0.5,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.primary.main}08)`,
                  transform: 'translateX(4px)',
                },
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => handleOpenFeedbackDialog('bug')}>
          <BugReportIcon sx={{ mr: 2, color: 'error.main' }} />
          Report Bug
        </MenuItem>
        <MenuItem onClick={() => handleOpenFeedbackDialog('feedback')}>
          <FeedbackIcon sx={{ mr: 2, color: 'primary.main' }} />
          Share Feedback
        </MenuItem>
      </Menu>

      {/* Feedback / Bug Report Dialog */}
      <FeedbackDialog
        open={feedbackDialogOpen}
        defaultMode={feedbackDialogMode}
        onClose={() => setFeedbackDialogOpen(false)}
      />

      {/* Notification Panel */}
      <NotificationPanel
        anchorEl={notificationAnchorEl}
        open={Boolean(notificationAnchorEl)}
        onClose={handleNotificationMenuClose}
        notifications={notifications}
        loading={notificationsLoading}
        loadingMore={notificationsLoadingMore}
        hasMore={notificationsHasMore}
        pageSize={NOTIFICATION_PAGE_SIZE}
        onMarkAsRead={handleMarkAsRead}
        onMarkAllAsRead={handleMarkAllAsRead}
        onDelete={handleDeleteNotification}
        onRefresh={refreshNotifications}
        onLoadMore={loadMoreNotifications}
      />

      <Box
        component="nav"
        sx={{
          width: { lg: currentDrawerWidth },
          flexShrink: { lg: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          '@media print': {
            display: 'none',
          },
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', lg: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3, md: 4 },
          width: { lg: `calc(100% - ${currentDrawerWidth}px)` },
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #fafbfc 0%, #f4f6f8 50%, #e8f4f2 100%)',
          position: 'relative',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          '@media print': {
            width: '100% !important',
            minHeight: 'auto',
            p: 0,
            background: 'white',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 80% 20%, rgba(144, 191, 69, 0.03) 0%, transparent 50%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Toolbar />
        <Box>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;